// The words of a run's line in Board activity (Fixes 8, David, 26 Oct: "say which agent made the change and what
// changed, not 'Ran hands-free'"). A run's line names the agent (its who) and says what it did, from the agent's own
// summary at the end of the run, as plain words: never "Ran hands-free", never the chat's first message, and never
// Markdown's marks (the feed shows a line as text, so "**Fixes 8**" showed its asterisks).

/** Markdown as plain words on one line: emphasis, code, links, headings, quotes and bullets lose their marks. */
export function plainWords(md: string): string {
  return (
    md
      // a fenced block keeps its words, without its fences
      .replace(/```[^\n]*\n?([\s\S]*?)```/g, " $1 ")
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
      .replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, "")
      .replace(/^[ \t]{0,3}>[ \t]?/gm, "")
      .replace(/^[ \t]*[-*+][ \t]+(?:\[[ xX]\][ \t]+)?/gm, "")
      .replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, "$2")
      .replace(/(^|[^\w*])\*(?=\S)([^*\n]*?\S)\*(?![\w*])/g, "$1$2")
      .replace(/(^|[^\w])_(?=\S)([^_\n]*?\S)_(?!\w)/g, "$1$2")
      .replace(/~~(?=\S)([\s\S]*?\S)~~/g, "$1")
      .replace(/`+([^`]+?)`+/g, "$1")
      .replace(/\s+/g, " ")
      .trim()
  );
}

/** Openings that say nothing of what was done: a word or two, then a stop or a dash ("Done.", "Sure thing!", "OK, ",
 *  "Done — "). "Finished the third fix" says something, so a word with no stop after it stays. */
const FILLER = /^(?:done|all done|all set|finished|ok(?:ay)?|sure(?: thing)?|great|perfect|got it|alright|right)(?:\s*[.!,:;]+|\s+[-\u2013\u2014]+)\s*/i;
/** A first sentence that only leads into what follows ("Here's what I changed:"). */
const LEAD_IN = /^(?:here(?:'s| is| are)|these are|this is what|below|summary|the changes|what i (?:did|changed))\b/i;

/**
 * What a run did, as its line's title, from the agent's summary: its first sentence, as "Added three fixes to Fixes
 * 8" for "I've added three fixes to **Fixes 8**, each with...", and the rest of the summary for the line's words. A
 * summary that opens on nothing to title (a lead-in, a question, nothing at all) gives no title, and its words stay
 * whole.
 */
export function runTitle(summary: string): { title: string | null; rest: string } {
  const plain = plainWords(summary);
  const body = plain.replace(FILLER, "").replace(/^[-\u2013\u2014]+\s*/, "");
  const first = body.match(/^[\s\S]+?[.!?:](?=\s|$)/)?.[0] ?? body;
  let t = first.replace(/[.!:]+$/, "").trim();
  if (!t || t.endsWith("?") || LEAD_IN.test(t)) return { title: null, rest: plain };
  t = t.replace(/^I(?:'ve| have|’ve)?\s+(?:just\s+|now\s+)?/i, "");
  if (t.length < 3) return { title: null, rest: plain };
  t = t[0].toUpperCase() + t.slice(1);
  if (t.length > 90) t = `${t.slice(0, 89).replace(/[\s,;]+\S*$/, "")}…`;
  return { title: t, rest: body.slice(first.length).trim() };
}

/** The counts a run's line opens its words with (worker/workspace.ts handsFreeLine). */
const COUNTS = /^(?:(?:No changes on the board|\d+ changes? on the board|\d+ calls? asked you first|Left out \d+ calls? that would send, delete or buy)\.\s*)+/;

/**
 * A hands-free run's line from before Fixes 8 ("Ran hands-free: “I want to add…”", its summary in Markdown), as the
 * lines are written now, so Board activity reads the same all the way down: a Button's or job's titled by its name, a
 * chat's by what its summary says it did, the words plain. Any other line is as it was.
 */
export function shownRunLine<T extends { title: string; body: string | null; itemId?: string | null; handsFree?: unknown }>(a: T): T {
  if (!a.handsFree || !a.title.startsWith("Ran hands-free: ")) return a;
  const name = a.title.slice("Ran hands-free: ".length).replace(/^[“"]|[”"]$/g, "");
  const counts = a.body?.match(COUNTS)?.[0]?.trim() ?? "";
  const sum = runTitle((a.body ?? "").slice(counts.length));
  const title = a.itemId ? `Ran “${name}”` : (sum.title ?? (counts.match(/^(\d+ changes?) on the board/)?.[1] ? `Made ${counts.match(/^(\d+ changes?)/)![1]}` : "Changed the board"));
  const body = [counts, a.itemId ? plainWords((a.body ?? "").slice(counts.length)) : sum.rest].filter(Boolean).join(" ") || null;
  return { ...a, title, body };
}
